// SCIM 2.0 admin page (v103). Configure the Azure Entra ID (or any
// SCIM 2.0 IdP) provisioning endpoint. Bearer token is shown ONCE
// at regeneration; only the hash is stored.

import { useState } from 'react';
import { Users, RefreshCw, Copy, Check, Power, AlertTriangle } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import {
  useSCIMSettings,
  setSCIMEnabled,
  regenerateSCIMToken,
} from '../../api/scim';

function scimStatusBadge(isLoading: boolean, enabled: boolean | undefined) {
  if (isLoading) {
    return <Badge tone="neutral">Loading…</Badge>;
  }
  if (enabled) {
    return (
      <Badge tone="success">
        <Users className="h-3 w-3" />
        Enabled
      </Badge>
    );
  }
  return <Badge tone="neutral">Disabled</Badge>;
}

export function AdminSCIM() {
  const { data, mutate, isLoading } = useSCIMSettings();
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onToggle = async () => {
    if (!data) return;
    setBusy(true);
    setErr(null);
    try {
      await setSCIMEnabled(!data.enabled);
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onRegen = async () => {
    if (
      !confirm(
        'Regenerate the SCIM bearer token? The current token will stop working immediately. The new token is shown ONCE — copy it before closing the dialog.',
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await regenerateSCIMToken();
      setToken(r.token);
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async () => {
    if (!token) return;
    await navigator.clipboard.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fullEndpoint =
    typeof globalThis !== 'undefined'
      ? `${globalThis.location.origin}/scim/v2`
      : '/scim/v2';

  return (
    <AdminShell
      title="SCIM provisioning"
      description="Standard-compliant SCIM 2.0 endpoint for IdP-driven user lifecycle. Azure Entra ID / Okta / Auth0 / OneLogin push user creates, group memberships, and de-provisions here. Disabling a user upstream propagates to the platform within seconds."
    >
      <Card>
        <CardHeader
          title="Status"
          actions={scimStatusBadge(isLoading, data?.enabled)}
        />
        <CardBody className="space-y-4">
          <div>
            <p className="orb-section-title text-fg-muted">Tenant URL (paste into IdP)</p>
            <p className="orb-code text-sm mt-1 break-all">{fullEndpoint}</p>
          </div>
          <div>
            <p className="orb-section-title text-fg-muted">Bearer token</p>
            <p className="text-sm text-fg-muted mt-1">
              {data?.has_token
                ? 'A token is configured. Use "Regenerate" if you need a new one.'
                : 'No token yet — click "Regenerate" to mint one.'}
            </p>
          </div>
          {data?.last_sync_at && (
            <div>
              <p className="orb-section-title text-fg-muted">Last activity</p>
              <p className="text-sm text-fg mt-1">
                {new Date(data.last_sync_at).toLocaleString()} —{' '}
                <span className="orb-code text-xs">{data.last_sync_op}</span>
              </p>
            </div>
          )}
          {err && <p className="text-danger font-mono text-sm">{err}</p>}
        </CardBody>
        <CardFooter className="flex justify-end gap-2">
          <Button variant="secondary" disabled={busy} onClick={onRegen}>
            <RefreshCw className="h-4 w-4" />
            Regenerate token
          </Button>
          <Button variant="primary" disabled={busy} onClick={onToggle}>
            <Power className="h-4 w-4" />
            {data?.enabled ? 'Disable' : 'Enable'}
          </Button>
        </CardFooter>
      </Card>

      {token && (
        <Card className="border-success/40 bg-success/5">
          <CardHeader
            title="New bearer token — copy it now"
            description="Plaintext is shown ONCE. Only the SHA-256 hash is stored. If you close this card without copying, you have to regenerate."
          />
          <CardBody>
            <div className="flex items-center gap-2 p-3 rounded-md bg-bg-inset border border-border font-mono text-sm break-all">
              <span className="flex-1">{token}</span>
              <button
                type="button"
                onClick={onCopy}
                className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-border hover:bg-bg-hover text-fg-muted hover:text-fg text-sm shrink-0"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </CardBody>
          <CardFooter className="flex justify-end">
            <Button variant="secondary" onClick={() => setToken(null)}>
              I have copied the token, dismiss
            </Button>
          </CardFooter>
        </Card>
      )}

      <Card>
        <CardHeader title="How to wire up Azure Entra ID" />
        <CardBody className="space-y-2 text-sm text-fg-muted">
          <p>1. In Azure: <span className="text-fg">Enterprise applications → your app → Provisioning</span>.</p>
          <p>2. Set <span className="text-fg">Provisioning Mode = Automatic</span>.</p>
          <p>3. <span className="text-fg">Tenant URL</span>: <span className="orb-code text-xs">{fullEndpoint}</span></p>
          <p>4. <span className="text-fg">Secret Token</span>: paste the bearer above.</p>
          <p>5. Click <span className="text-fg">Test connection</span> — Entra calls <span className="orb-code text-xs">GET /scim/v2/ServiceProviderConfig</span> and expects 200.</p>
          <p>6. Save, set scope (assigned users / all), and start provisioning.</p>
          <div className="rounded-md bg-warning/5 border border-warning/30 p-3 flex items-start gap-2 mt-4">
            <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
            <p className="text-fg-muted">
              When a user is <span className="text-fg font-medium">disabled or deleted in Entra</span>,
              SCIM sends a PATCH <span className="orb-code">active=false</span> (or DELETE).
              We flip <span className="orb-code">users.active</span> and the auth middleware
              refuses the next request. Existing sessions also fail on the next API call.
            </p>
          </div>
        </CardBody>
      </Card>
    </AdminShell>
  );
}
